import { z } from 'zod';
import { isIsoCountryCode } from './countries.js';
import { phoneSchema, storedPhoneSchema } from '../phone/phone.js';
/**
 * Parties of a deposit (cash-deposits.md section 2). The deposit keeps a
 * snapshot of every party as entered at that moment; later edits to the
 * customer file in Customers never change it.
 */
export const DOCUMENT_TYPES = ['NATIONAL_ID', 'PASSPORT'];
export const documentTypeSchema = z.enum(DOCUMENT_TYPES);
/**
 * Every party phone (depositor, intended recipient, actual recipient), typed
 * on the tablet or by the employee, follows the shared phone rule of
 * customers.md section 9 (owner decision, 23 October 2026): a mobile only,
 * normalised and stored in E.164; a landline is refused with
 * PHONE_NOT_MOBILE. The schema is `phoneSchema` of `../phone/phone.ts`.
 */
/** An optional phone: empty or missing becomes null; anything else follows the phone rule. */
export const optionalPhoneSchema = z.preprocess((value) => value === undefined || (typeof value === 'string' && value.trim() === '') ? null : value, phoneSchema.nullable());
/**
 * An optional phone of an edited deposit: like `optionalPhoneSchema`, but a
 * stored phone of an excluded country is read too; the edit service refuses
 * it with PHONE_COUNTRY_NOT_ALLOWED only when it changed (customers.md
 * section 9.7).
 */
export const optionalStoredPhoneSchema = z.preprocess((value) => value === undefined || (typeof value === 'string' && value.trim() === '') ? null : value, storedPhoneSchema.nullable());
/** ISO 3166-1 alpha-2 issuing country, from the tablet service's list. */
export const countryCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, 'must be an ISO alpha-2 country code')
    .refine(isIsoCountryCode, 'must be an ISO alpha-2 country code');
export const fullNameSchema = z.string().trim().min(1).max(200);
export const documentNumberSchema = z.string().trim().min(1).max(64);
/**
 * The depositor or the actual recipient: every field is required
 * [DEP-02, DEL-03]. A passport is accepted wherever a document is required.
 */
export const partyIdentitySchema = z.object({
    fullName: fullNameSchema,
    phone: phoneSchema,
    documentType: documentTypeSchema,
    documentNumber: documentNumberSchema,
    documentCountry: countryCodeSchema,
});
/**
 * A party identity as the API shows it (tablet submissions and prefills):
 * the phone is the stored text, so a value saved before the phone rule of
 * 23 October 2026 is still displayed rather than failing the response.
 */
export const partyIdentityViewSchema = partyIdentitySchema.extend({ phone: z.string() });
/**
 * The intended recipient, entered when "معلومات المستلم متوفرة" is on
 * [DEP-03]. A name without a phone is allowed; such a deposit gets no
 * recipient message. Kept on the deposit only, never sent to Customers.
 */
export const intendedRecipientSchema = z.object({
    fullName: fullNameSchema,
    phone: optionalPhoneSchema.optional(),
});
export const intendedRecipientViewSchema = z.object({
    fullName: z.string(),
    phone: z.string().nullable(),
});
/** A party as stored on the deposit. */
export const partySnapshotSchema = z.object({
    fullName: z.string(),
    phone: z.string(),
    documentType: documentTypeSchema,
    documentNumber: z.string(),
    documentCountry: z.string(),
    /** Customers id once registered; null while the outbox is pending. */
    customerId: z.string().nullable(),
    /**
     * Customers refused the document because it belongs to another customer
     * (409); only phone and name were registered. The details show
     * "الوثيقة مسجلة لعميل آخر" beside the party.
     */
    documentConflict: z.boolean(),
    /** رقم مؤكد: the phone was verified with a WhatsApp code, or already verified in Customers. */
    phoneVerified: z.boolean(),
});
export const SIGNATURE_PARTIES = ['DEPOSITOR', 'RECIPIENT'];
export const signaturePartySchema = z.enum(SIGNATURE_PARTIES);
/**
 * The Customers platform's answer for a phone, shown on the employee screen
 * so the employee can compare. Never shown on the tablet.
 */
export const customerMatchSchema = z.object({
    status: z.enum(['FOUND', 'NOT_FOUND', 'UNAVAILABLE']),
    customer: z
        .object({
        id: z.string(),
        phone: z.string(),
        name: z.string().nullable(),
        level: z.enum(['A', 'B', 'C']),
    })
        .nullable(),
});
//# sourceMappingURL=parties.js.map